/**
 * ProductReferencePicker — the one canonical product picker in PanLuma.
 *
 * Walks the five reference layers and emits a complete `ProductReference`:
 *   1. Product     — searchable popover, grouped by category.
 *   2. Variant     — one selector per variant dimension; the combination is
 *                    resolved to a `variant_id` via `resolve_or_create_variant`
 *                    (lazy materialisation). Shown only when `enable_variants`
 *                    is on and the product has dimensions.
 *   3. Packaging   — the packaging UoM; null = base unit. Shown only when the
 *                    product has packagings.
 *   4. Lot         — `mode='pick-lot'`: choose an existing stock lot (FEFO).
 *                    `mode='create-lot'`: enter a lot code + expiry +
 *                    stock-tracked configuration values, then create the lot.
 *                    `mode='catalog-only'` (default): skipped.
 *   5. Post-pick configs — `inventory_tracked=false` configurations: free
 *                    input per type. Shown only when `enable_configurations`
 *                    is on. Emitted as `configuration_selections`.
 *
 * Replaces ProductPicker and ProductWithPackagingPicker. Pass `productOnly`
 * for the plain product-selection cases (filters, category assignment, a ticket's
 * related product) where only `product_id` is wanted and no sub-layers render.
 * `PackagingPicker` is retained as the standalone packaging sub-picker for
 * contexts where the product is already fixed (price-list entry forms).
 *
 * Visual hierarchy: in the stacked dialog layout (`showLabels`, the default) the
 * variant/packaging/lot/config sub-layers are grouped under an indented left-rail
 * so they read as *secondary to* the selected product instead of as more
 * peer-level form fields sitting beside the dialog's other inputs (warehouse,
 * quantity, …). The compact inline/line-item layout (`showLabels={false}`,
 * the horizontal exception called out in the contract) keeps the flat stack — the
 * rail belongs to the roomy vertical form, not a dense table cell.
 * See docs/modules/master-data/product-reference-contract.md.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Boxes, Check, ChevronsUpDown, Loader2, Package, Plus, RefreshCw, Search, Truck } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { filterAndSortBySearch } from '../../../../shared/utils/searchScore';
import { stripHtml } from '../../../../shared/utils/sanitize';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Checkbox } from '../../../../components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Badge } from '../../../../components/ui/badge';
import {
  BidiText,
  FacetOptionRow,
  LtrToken,
  resolveCandidateRows,
  type EntityFilterCandidates,
} from '../../../../shared/components/common';
import { toast } from 'sonner';
import { useStockLots, useCreateStockLot } from '../../../../shared/inventory/hooks/useStock';
import {
  useProducts,
  useProductCategories,
  useProductReferenceFallbackProduct,
  useProductReferenceLayers,
  useResolveVariant,
  useProductCapabilities,
} from '../hooks';
import { useListOfferingsApiV1MasterDataOfferingsGet } from '../../../../generated/api/offerings/offerings';
import type { Offering } from '../../../../generated/api/models/offering';
import type {
  Product,
  ProductCategory,
  ProductConfiguration,
  ProductReference,
  ProductConfigurationSelection,
  ProductVariant,
} from '../types';
import { emptyProductReference, emptyServiceOfferingReference } from '../types';
import { ProductQuickCreateDialog } from './ProductQuickCreateDialog';

export type ProductReferenceMode = 'catalog-only' | 'pick-lot' | 'create-lot';

export interface ProductReferencePickerProps extends EntityFilterCandidates {
  /** The current reference, or null when nothing is selected. */
  value: ProductReference | null;
  /** Emitted whenever any layer changes. Null when the product is cleared. */
  onChange: (next: ProductReference | null) => void;
  /** Lot behaviour. Default `catalog-only` (no lot layer). */
  mode?: ProductReferenceMode;
  disabled?: boolean;
  /** Show a label above each layer (default true). */
  showLabels?: boolean;
  /** Label for the product layer. */
  productLabel?: string;
  /**
   * Mark the product layer's label required (STYLE_GUIDE § Required Fields).
   * Pass it wherever the picker's value gates a dialog's submit — an unmarked
   * field that blocks Submit reads as a dead button with no stated cause. Only
   * takes effect with `showLabels` (the default): the marker is rendered BY the
   * label, so a caller whose visible label is a sibling `<Label>` marks that one.
   */
  required?: boolean;
  /**
   * Product status filter for the product list. Defaults to `['active']` — only
   * an active product is transactable, so draft / planned / archived /
   * discontinued products never surface in a picker unless a caller explicitly
   * opts them in (e.g. a catalogue-curation context).
   */
  productStatusFilter?: string[];
  /** Product IDs to exclude from the product list. */
  excludeProductIds?: string[];
  /**
   * Scope what the picker OFFERS to ONE company — the read door of the
   * catalog-scope rule. A line editor passes the DOCUMENT's own `company_id`,
   * never the app bar's: the write side refuses a line naming a product outside
   * the document's company, so offering one here only defers the refusal to
   * save. Products carrying no company tag are shared and stay offered. Omit it
   * outside a document context to keep the app-bar scope.
   *
   * It bounds the offer list ONLY. The picker's stockability lookup stays
   * unscoped on purpose, so a product a line ALREADY names is still classified
   * correctly even when it sits outside this company.
   */
  companyId?: string | null;
  /** Restrict the product list to products without a category or in this category. */
  allowedCategoryId?: string | null;
  /** Width of the product search popover. */
  popoverWidth?: number;
  /** Render only the product layer — no variant/packaging/lot/config layers. */
  productOnly?: boolean;
  /**
   * Offer an inline "+ Create {{product}}" action in the product dropdown that
   * opens the ProductQuickCreateDialog and auto-selects the new product. Opt-in
   * (default false) so filter / placement contexts stay exempt per the
   * entity-picker inline-create policy. Enable in line-item editors.
   */
  allowCreate?: boolean;
  /**
   * Surface product-less `shipment_service` offerings (freight services) in the
   * product dropdown, under a "Freight & services" group. Opt-in (default false)
   * — only commercial line-item editors that sell freight enable it. Selecting a
   * service offering emits a product-less reference anchored on `offering_id`
   * (freight-as-product §1); the product sub-layers stay hidden.
   */
  enableServiceOfferings?: boolean;
  /**
   * Render the operational-instance sub-step for a selected service offering
   * (e.g. pick/create the QUOTED shipment that realizes a freight line). Invoked
   * with the selected offering id; the returned node renders inside the picker's
   * indented sub-layer rail. Keeps the picker vertical-agnostic — the consumer
   * owns the shipments-specific binding UI. Only used when a service offering is
   * selected and `enableServiceOfferings` is on.
   */
  renderServiceInstance?: (offeringId: string) => ReactNode;
  /** Extra classes for the root container. */
  className?: string;
}

const BASE_PACKAGING = '__base__';

/** Sum the fixed-type price adjustments a post-pick selection resolves to, in
 *  DECIMAL dollars. The source config price_adjustment is DECIMAL dollars and
 *  the selection snapshot this feeds is DECIMAL dollars too (the catalog/order
 *  money convention, catalog migrated), so each fixed amount passes through. */
function computeAdjustment(config: ProductConfiguration, values: string[]): number {
  if (config.config_type === 'select' || config.config_type === 'multi_select') {
    return values.reduce((sum, v) => {
      const cv = config.values.find((x) => x.value === v);
      if (cv && cv.price_adjustment_type === 'fixed' && cv.price_adjustment) {
        return sum + cv.price_adjustment;
      }
      return sum;
    }, 0);
  }
  if (config.price_adjustment_type === 'fixed' && config.price_adjustment) {
    return config.price_adjustment;
  }
  return 0;
}

/** Compose the human-readable rendered value for a selection. */
function composeDisplayValue(config: ProductConfiguration, values: string[]): string {
  if (config.config_type === 'select' || config.config_type === 'multi_select') {
    return values
      .map((v) => config.values.find((x) => x.value === v)?.label ?? v)
      .filter(Boolean)
      .join(', ');
  }
  return values[0] ?? '';
}

/** Build a ProductConfigurationSelection snapshot, or null when no value is set. */
function buildSelection(
  config: ProductConfiguration,
  values: string[],
): ProductConfigurationSelection | null {
  const cleaned = values.filter((v) => v !== '');
  if (cleaned.length === 0) return null;
  return {
    configuration_id: config.id,
    configuration_code: config.code,
    config_type: config.config_type,
    values: cleaned,
    display_value: composeDisplayValue(config, cleaned),
    price_adjustment: computeAdjustment(config, cleaned),
  };
}

// ---------------------------------------------------------------- product layer

interface ProductSearchFieldProps {
  value: string | null;
  /** Emits the chosen product id and its offering handle (OFF-2), or (null, null) when cleared. */
  onSelect: (productId: string | null, offeringId: string | null) => void;
  disabled?: boolean;
  statusFilter: string[];
  excludeProductIds: string[];
  allowedCategoryId?: string | null;
  /** The document's own company, or null/undefined for the app-bar scope. */
  companyId?: string | null;
  popoverWidth: number;
  placeholder: string;
  /** Show the inline "+ Create {{product}}" action (and quick-create dialog). */
  allowCreate?: boolean;
  /** Surface `shipment_service` offerings under a "Freight & services" group. */
  enableServiceOfferings?: boolean;
  /** The currently-selected service offering id (product-less ref), or null. */
  serviceOfferingId?: string | null;
  /** Emits the chosen service offering (product-less freight line). */
  onSelectService?: (offering: Offering) => void;
  candidates?: EntityFilterCandidates['candidates'];
  onShowAllCandidates?: EntityFilterCandidates['onShowAllCandidates'];
  showAllCandidatesLabel?: EntityFilterCandidates['showAllCandidatesLabel'];
}

function ProductSearchField({
  value,
  onSelect,
  disabled,
  statusFilter,
  excludeProductIds,
  allowedCategoryId,
  companyId,
  popoverWidth,
  placeholder,
  allowCreate = false,
  enableServiceOfferings = false,
  serviceOfferingId = null,
  onSelectService,
  candidates,
  onShowAllCandidates,
  showAllCandidatesLabel,
}: ProductSearchFieldProps) {
  const { t } = useTranslation(['products', 'common']);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const { data: products = [], isError: productsError, refetch: refetchProducts } =
    useProducts({ company_id: companyId ?? undefined });
  const { data: categories = [] } = useProductCategories();
  // Only active offerings are sellable; the query is disabled entirely unless
  // this picker opts into service offerings (shares the cache key across lines).
  const { data: serviceOfferings = [] } = useListOfferingsApiV1MasterDataOfferingsGet(
    { kind: 'shipment_service', is_active: true },
    { query: { enabled: enableServiceOfferings } },
  );

  const filteredServiceOfferings = useMemo(
    () =>
      enableServiceOfferings
        ? filterAndSortBySearch(serviceOfferings, search, (o) => o.label)
        : [],
    [enableServiceOfferings, serviceOfferings, search],
  );
  const selectedService = useMemo(
    () => (serviceOfferingId ? serviceOfferings.find((o) => o.id === serviceOfferingId) ?? null : null),
    [serviceOfferings, serviceOfferingId],
  );

  const categoryMap = useMemo(() => {
    const map = new Map<string, ProductCategory>();
    for (const category of categories) map.set(category.id, category);
    return map;
  }, [categories]);

  const available = useMemo(
    () =>
      products.filter((p) => {
        if (statusFilter.length > 0 && !statusFilter.includes(p.status)) return false;
        if (excludeProductIds.includes(p.id)) return false;
        const categoryId = p.product_category_ids?.[0] ?? null;
        if (allowedCategoryId !== undefined && categoryId && categoryId !== allowedCategoryId) {
          return false;
        }
        return true;
      }),
    [products, statusFilter, excludeProductIds, allowedCategoryId],
  );

  const filtered = useMemo(
    () =>
      filterAndSortBySearch(available, search, (p) =>
        // description is rich-text HTML — strip tags so the search index matches
        // on the readable text, not markup like "<p>" or "<strong>".
        [p.name, p.sku, stripHtml(p.description ?? '')].filter(Boolean).join(' '),
      ),
    [available, search],
  );

  // OFF-10: split bundles into their own "Bundles" group so they are no longer
  // filed under the products-only "Other Products" heading. Products are still
  // grouped by category; the null-category product bucket keeps the "Other" heading.
  const grouped = useMemo(() => {
    const bundles: Product[] = [];
    const groups = new Map<string | null, Product[]>();
    for (const product of filtered) {
      if (product.product_type === 'bundle') {
        bundles.push(product);
        continue;
      }
      const categoryId = product.product_category_ids?.[0] ?? null;
      if (!groups.has(categoryId)) groups.set(categoryId, []);
      groups.get(categoryId)!.push(product);
    }
    const categoryGroups = Array.from(groups.entries())
      .filter(([categoryId]) => categoryId !== null)
      .map(([categoryId, prods]) => ({
        key: categoryId!,
        kind: 'product' as const,
        categoryName: categoryMap.get(categoryId!)?.name ?? null,
        products: prods,
      }))
      .sort((a, b) => (a.categoryName ?? '').localeCompare(b.categoryName ?? ''));
    const ungrouped = groups.get(null);
    const result: {
      key: string;
      kind: 'product' | 'bundle';
      categoryName: string | null;
      products: Product[];
    }[] = [...categoryGroups];
    if (ungrouped && ungrouped.length > 0) {
      result.push({ key: '__ungrouped__', kind: 'product', categoryName: null, products: ungrouped });
    }
    if (bundles.length > 0) {
      result.push({ key: '__bundles__', kind: 'bundle', categoryName: null, products: bundles });
    }
    return result;
  }, [filtered, categoryMap]);

  const listSelected = useMemo(() => products.find((p) => p.id === value), [products, value]);
  // A custom one-off (is_custom_one_off) is excluded from the catalog list, so a
  // line already referencing it — a manually-added custom item or a finalized
  // inquiry line — finds nothing above. Resolve that one product so the trigger
  // shows its name instead of the "select a product" placeholder: from the
  // batched mount when a line-item editor supplied one, else a single standalone
  // fetch (only when the value isn't already in the loaded list).
  const fallbackSelected = useProductReferenceFallbackProduct(value, !!value && !listSelected);
  const selected = listSelected ?? fallbackSelected ?? undefined;
  const facetRows = useMemo(
    () =>
      candidates
        ? resolveCandidateRows({
            candidates,
            search,
            value: value ?? undefined,
            selectedLabel: selected?.name,
          })
        : null,
    [candidates, search, selected?.name, value],
  );
  const hasCategories = categories.length > 0;

  // OFF-9: surface the offering kind on the collapsed trigger so a selected
  // bundle is distinguishable from a product without opening the dropdown —
  // same Boxes/Package icon + Bundle badge as ProductReferenceDisplay. A
  // product-less service offering shows the Truck icon (the shipment_service
  // offering-kind glyph, matching ProductReferenceDisplay) + a "Service" badge.
  const isBundle = selected?.product_type === 'bundle';
  const isService = !selected && !!selectedService;
  const KindIcon = isService ? Truck : isBundle ? Boxes : Package;

  const handleSelect = (productId: string) => {
    const product = productId ? products.find((p) => p.id === productId) : undefined;
    onSelect(productId || null, product?.offering_id ?? null);
    setOpen(false);
    setSearch('');
  };

  const handleSelectService = (offering: Offering) => {
    onSelectService?.(offering);
    setOpen(false);
    setSearch('');
  };

  // Inline create: carry the typed search into the quick-create dialog as the
  // product name, then auto-select the freshly created product on success.
  const handleAddNew = () => {
    setCreateName(search.trim());
    setOpen(false);
    setShowCreate(true);
  };

  const handleProductCreated = (product: Product) => {
    onSelect(product.id, product.offering_id ?? null);
    setSearch('');
  };

  const trimmedSearch = search.trim();
  const createLabel = trimmedSearch
    ? t('products:reference.createNamedProduct', { name: trimmedSearch })
    : t('products:reference.createProduct');

  const hasSelection = !!selected || isService;
  const label = selected
    ? selected.sku
      ? `${selected.name} (${selected.sku})`
      : selected.name
    : isService
      ? selectedService!.label
      : placeholder;

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', !hasSelection && 'text-muted-foreground')}
        >
          <span className="flex min-w-0 items-center gap-2">
            {hasSelection && (
              // For a bundle/service the visible badge already conveys the kind,
              // so the icon is decorative; for a product (no badge) the label
              // carries the kind cue.
              <KindIcon
                className="h-4 w-4 shrink-0 text-muted-foreground"
                aria-hidden={isBundle || isService || undefined}
                aria-label={isBundle || isService ? undefined : t('products:reference.product')}
              />
            )}
            <span className="truncate"><BidiText>{label}</BidiText></span>
            {isBundle && (
              <Badge variant="secondary" className="shrink-0 text-xs font-normal">
                {t('products:reference.bundle')}
              </Badge>
            )}
            {isService && (
              <Badge variant="secondary" className="shrink-0 text-xs font-normal">
                {t('products:reference.service')}
              </Badge>
            )}
          </span>
          <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start" style={{ width: popoverWidth }}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('products:categories.searchProducts')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {productsError ? (
              // An API error must not look like an empty catalogue: surface a
              // real error + retry instead of the "No products found" empty state.
              <div className="px-3 py-6 text-center">
                <AlertCircle className="mx-auto mb-2 h-5 w-5 text-destructive" aria-hidden />
                <p className="mb-3 text-sm text-muted-foreground">
                  {t('products:reference.loadError')}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void refetchProducts()}
                >
                  <RefreshCw className="me-2 h-4 w-4" />
                  {t('common:app.retry')}
                </Button>
              </div>
            ) : (
              <>
                {facetRows === null && (
                  <CommandEmpty>{t('products:categories.noProductsFound')}</CommandEmpty>
                )}
                {facetRows !== null && facetRows.length === 0 && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {t('products:categories.noProductsFound')}
                  </div>
                )}
                {(value || serviceOfferingId) && (
              <CommandGroup>
                <CommandItem value="__clear__" onSelect={() => handleSelect('')}>
                  <Check className="me-2 h-4 w-4 opacity-0" />
                  <span className="text-muted-foreground">{t('common:app.none')}</span>
                </CommandItem>
              </CommandGroup>
            )}
            {facetRows !== null && facetRows.length > 0 && (
              <CommandGroup>
                {facetRows.map((row) => (
                  <CommandItem
                    key={row.id}
                    value={row.id}
                    onSelect={() => handleSelect(row.id)}
                  >
                    <FacetOptionRow label={row.label} count={row.count} />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {facetRows === null && grouped.map((group, index) => (
              <div key={group.key}>
                {(index > 0 || !!value) && <CommandSeparator />}
                <CommandGroup
                  heading={
                    group.kind === 'bundle'
                      ? t('products:categories.bundlesGroup')
                      : group.categoryName ?? (hasCategories ? t('products:categories.otherProducts') : undefined)
                  }
                >
                  {group.products.map((product) => {
                    const itemIsBundle = group.kind === 'bundle';
                    const ItemKindIcon = itemIsBundle ? Boxes : Package;
                    return (
                      <CommandItem
                        key={product.id}
                        value={product.id}
                        onSelect={() => handleSelect(product.id)}
                      >
                        <Check
                          className={cn('me-2 h-4 w-4', value === product.id ? 'opacity-100' : 'opacity-0')}
                        />
                        <ItemKindIcon
                          className="me-2 h-4 w-4 shrink-0 text-muted-foreground"
                          aria-hidden={itemIsBundle || undefined}
                          aria-label={itemIsBundle ? undefined : t('products:reference.product')}
                        />
                        <span className="truncate font-medium"><BidiText>{product.name}</BidiText></span>
                        {product.sku && (
                          <span className="ms-2 shrink-0 text-xs text-muted-foreground">
                            <LtrToken>({product.sku})</LtrToken>
                          </span>
                        )}
                        {itemIsBundle && (
                          <Badge variant="secondary" className="ms-2 shrink-0 text-xs font-normal">
                            {t('products:reference.bundle')}
                          </Badge>
                        )}
                        {product.status === 'draft' && (
                          <span className="ms-2 shrink-0 text-xs text-warning">
                            ({t('products:productStatuses.draft')})
                          </span>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </div>
            ))}
            {facetRows !== null && onShowAllCandidates && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value="__show_all_candidates__"
                    onSelect={onShowAllCandidates}
                    className="text-muted-foreground touch:h-11"
                  >
                    <Search className="me-2 h-4 w-4 shrink-0" />
                    {showAllCandidatesLabel ?? t('common:app.showAllOptions')}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
            {enableServiceOfferings && filteredServiceOfferings.length > 0 && (
              <div key="__services__">
                {(grouped.length > 0 || !!value || !!serviceOfferingId) && <CommandSeparator />}
                <CommandGroup heading={t('products:reference.freightServicesGroup')}>
                  {filteredServiceOfferings.map((offering) => (
                    <CommandItem
                      key={offering.id}
                      value={`svc:${offering.id}`}
                      onSelect={() => handleSelectService(offering)}
                    >
                      <Check
                        className={cn(
                          'me-2 h-4 w-4',
                          serviceOfferingId === offering.id ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <Truck className="me-2 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                      <span className="truncate font-medium"><BidiText>{offering.label}</BidiText></span>
                      <Badge variant="secondary" className="ms-2 shrink-0 text-xs font-normal">
                        {t('products:reference.service')}
                      </Badge>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </div>
            )}
            {allowCreate && (
              <>
                {(grouped.length > 0 || !!value) && <CommandSeparator />}
                <CommandGroup>
                  <CommandItem value="__create__" onSelect={handleAddNew}>
                    <Plus className="me-2 h-4 w-4" />
                    <span className="truncate font-medium">{createLabel}</span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
    {allowCreate && (
      <ProductQuickCreateDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleProductCreated}
        defaultName={createName}
        skipNavigation
      />
    )}
    </>
  );
}

// ------------------------------------------------------------- config field input

interface ConfigFieldInputProps {
  config: ProductConfiguration;
  values: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}

/** Renders one configuration field by `config_type`. Shared by post-pick + lot configs. */
function ConfigFieldInput({ config, values, onChange, disabled }: ConfigFieldInputProps) {
  const { t } = useTranslation('products');
  const single = values[0] ?? '';

  switch (config.config_type) {
    case 'select':
      return (
        <Select value={single || undefined} onValueChange={(v) => onChange([v])} disabled={disabled}>
          <SelectTrigger>
            <SelectValue placeholder={t('reference.selectOption')} />
          </SelectTrigger>
          <SelectContent>
            {config.values.map((cv) => (
              <SelectItem key={cv.id} value={cv.value}>
                {cv.label ?? cv.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'multi_select':
      return (
        <div className="space-y-1.5">
          {config.values.map((cv) => {
            const checked = values.includes(cv.value);
            return (
              <label key={cv.id} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={(next) =>
                    onChange(
                      next === true
                        ? [...values, cv.value]
                        : values.filter((v) => v !== cv.value),
                    )
                  }
                />
                <span>{cv.label ?? cv.value}</span>
              </label>
            );
          })}
        </div>
      );
    case 'number':
      return (
        <Input
          type="number"
          value={single}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
        />
      );
    case 'date':
      return (
        <Input
          type="date"
          value={single}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
        />
      );
    default:
      return (
        <Input
          value={single}
          disabled={disabled}
          placeholder={config.name}
          onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
        />
      );
  }
}

/**
 * FlatVariantField — Layer 2 for a product that models variants as named SKU
 * siblings instead of dimension axes (flat-variant-design.md §6).
 *
 * There is nothing to resolve here: flat variants are pre-created by the product
 * admin, so picking one sets `variant_id` directly. Searchable because the cap is
 * up to `max_variants_per_product` siblings.
 */
function FlatVariantField({
  variants,
  value,
  onSelect,
  disabled,
  popoverWidth,
}: {
  variants: ProductVariant[];
  value: string | null;
  onSelect: (variantId: string | null) => void;
  disabled?: boolean;
  popoverWidth: number;
}) {
  const { t } = useTranslation(['products', 'common']);
  const [open, setOpen] = useState(false);
  const selected = variants.find((v) => v.id === value) ?? null;

  const handleSelect = (variantId: string | null) => {
    setOpen(false);
    onSelect(variantId);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid="flat-variant-trigger"
          className={cn('w-full justify-between font-normal', !selected && 'text-muted-foreground')}
        >
          <span className="truncate">
            {selected ? (
              <BidiText>{selected.name}</BidiText>
            ) : (
              t('products:reference.selectVariant')
            )}
          </span>
          <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start" style={{ width: popoverWidth }}>
        <Command>
          <CommandInput placeholder={t('products:reference.searchVariants')} />
          <CommandList>
            <CommandEmpty>{t('products:reference.noVariantsFound')}</CommandEmpty>
            {selected && (
              <CommandGroup>
                <CommandItem value="__clear__" onSelect={() => handleSelect(null)}>
                  <Check className="me-2 h-4 w-4 opacity-0" />
                  <span className="text-muted-foreground">{t('common:app.none')}</span>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {variants.map((variant) => (
                <CommandItem
                  key={variant.id}
                  value={`${variant.name} ${variant.sku ?? ''}`}
                  onSelect={() => handleSelect(variant.id)}
                >
                  <Check
                    className={cn(
                      'me-2 h-4 w-4 shrink-0',
                      variant.id === value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                    <span className="truncate"><BidiText>{variant.name}</BidiText></span>
                    {variant.sku && (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        <LtrToken>{variant.sku}</LtrToken>
                      </span>
                    )}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// --------------------------------------------------------------------- main picker

export function ProductReferencePicker({
  value,
  onChange,
  mode = 'catalog-only',
  disabled,
  showLabels = true,
  productLabel,
  required = false,
  productStatusFilter = ['active'],
  excludeProductIds = [],
  allowedCategoryId,
  companyId,
  popoverWidth = 350,
  productOnly = false,
  allowCreate = false,
  enableServiceOfferings = false,
  renderServiceInstance,
  className,
  candidates,
  onShowAllCandidates,
  showAllCandidatesLabel,
}: ProductReferencePickerProps) {
  const { t } = useTranslation(['products', 'common']);
  const productId = value?.product_id ?? null;
  // A product-less reference anchored on offering_id alone is a vertical service
  // offering (a kind='shipment_service' freight line — freight-as-product §1).
  const serviceOfferingId = value && value.product_id === null ? value.offering_id ?? null : null;
  const isServiceReference = serviceOfferingId !== null;

  // The two layer switches, off the member-open capabilities read. The admin
  // tenant-settings route 403s for a plain member, so reading them there left
  // both `false` on every non-admin line — the variant and configuration
  // pickers simply never appeared. Both fields are required and non-null here;
  // `undefined` means only "still in flight", and a layer that has not resolved
  // yet renders as it always has (nothing) for the one round trip it takes.
  const { data: capabilities } = useProductCapabilities();
  const variantsEnabled = capabilities?.enable_variants === true;
  const configsEnabled = capabilities?.enable_configurations === true;

  // OFF-11: a bundle (and future non-stockable vertical offerings) is not
  // directly stockable — its availability derives from its component products —
  // so the lot layer (pick/create) is semantically wrong on a bundle line.
  // Subscribe to the product list to detect the non-stockable case.
  //
  // Deliberately NOT company-scoped, unlike the OFFER list inside
  // ProductSearchField: this read CLASSIFIES a product the line already names,
  // and a line may legitimately carry one outside the document's company — a row
  // that predates the read-door fix, or one an import wrote. Scoping it would
  // drop that product out of the array and silently misclassify the line as
  // stockable (OFF-11: a bundle's lot layer is semantically wrong), which is a
  // correctness gap the extra cached request does not justify.
  const { data: products = [] } = useProducts();
  const selectedProduct = useMemo(
    () => (productId ? products.find((p) => p.id === productId) ?? null : null),
    [products, productId],
  );
  const isNonStockableOffering = selectedProduct?.product_type === 'bundle';

  // The four sub-layer reads. Under a line-item editor's
  // `ProductCatalogLayersProvider` these come from ONE batched request for the
  // whole document; standalone they are the same per-product queries as before.
  // The gates suppress a read whose layer cannot reach the screen at all — pure
  // waste otherwise, and waste paid per line.
  const { packagings, dimensions, variants, configurations } = useProductReferenceLayers(
    productId,
    {
      // `productOnly` renders no sub-layer at all.
      packagings: !productOnly,
      // Both variant spellings (dimension axes and flat siblings) are gated on
      // the tenant's `enable_variants`, so with it off neither can render.
      variants: !productOnly && variantsEnabled,
      // Post-pick config fields need `enable_configurations`. The same query also
      // carries the stock-tracked configs the create-lot form renders
      // (`lotConfigs`) — its only other reader, hence `create-lot` and not every
      // non-catalog-only mode.
      configurations: !productOnly && (configsEnabled || mode === 'create-lot'),
    },
  );
  const resolveVariant = useResolveVariant();
  const createLot = useCreateStockLot();

  const postPickConfigs = useMemo(
    () => configurations.filter((c) => !c.inventory_tracked && c.status === 'active'),
    [configurations],
  );
  const lotConfigs = useMemo(
    () => configurations.filter((c) => c.inventory_tracked && c.status === 'active'),
    [configurations],
  );

  // Flat variants (named SKU siblings) are the alternative to dimension axes: a
  // product has one model or the other, never both (flat-variant-design.md §5).
  const flatVariants = useMemo(
    () => variants.filter((v) => v.variant_type === 'flat' && v.status === 'active'),
    [variants],
  );
  const showVariants =
    !productOnly && variantsEnabled && (dimensions.length > 0 || flatVariants.length > 0);
  const showPackaging = !productOnly && packagings.length > 0;
  // OFF-11: never offer lot pick/create for a non-stockable offering (bundle).
  const showLot = !productOnly && mode !== 'catalog-only' && !isNonStockableOffering;
  const showBundleFulfilmentNote = !productOnly && mode !== 'catalog-only' && isNonStockableOffering;
  const showPostPick = !productOnly && configsEnabled && postPickConfigs.length > 0;

  // --- variant dimension selectors ------------------------------------------
  const currentVariant = useMemo(
    () => variants.find((v) => v.id === value?.variant_id) ?? null,
    [variants, value?.variant_id],
  );
  const [dimSelections, setDimSelections] = useState<Record<string, string>>({});
  const syncedVariantId = useRef<string | null | undefined>(undefined);

  // Reset selectors when the product changes.
  useEffect(() => {
    setDimSelections({});
    syncedVariantId.current = undefined;
  }, [productId]);

  // Back-fill selectors from a committed variant (initial load / external set).
  useEffect(() => {
    if (!value?.variant_id || syncedVariantId.current === value.variant_id) return;
    if (!currentVariant) return;
    const map: Record<string, string> = {};
    for (const opt of currentVariant.options) map[opt.dimension_id] = opt.value_id;
    setDimSelections(map);
    syncedVariantId.current = value.variant_id;
  }, [value?.variant_id, currentVariant]);

  const handleDimensionChange = async (dimensionId: string, valueId: string) => {
    // The variant layer only renders once a product is picked, so product_id is
    // non-null here; capture it into a local so it survives the await below.
    if (!value?.product_id) return;
    const productIdForVariant = value.product_id;
    const next = { ...dimSelections, [dimensionId]: valueId };
    setDimSelections(next);
    const complete = dimensions.every((d) => next[d.id]);
    if (!complete) {
      onChange({ ...value, variant_id: null, lot_id: null });
      return;
    }
    try {
      const variant = await resolveVariant.mutateAsync({ productId: productIdForVariant, selections: next });
      syncedVariantId.current = variant.id;
      onChange({ ...value, variant_id: variant.id, lot_id: null });
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  // --- lot creation (create-lot mode) ---------------------------------------
  const [lotCode, setLotCode] = useState('');
  const [lotExpiry, setLotExpiry] = useState('');
  const [lotConfigValues, setLotConfigValues] = useState<Record<string, string[]>>({});

  useEffect(() => {
    setLotCode('');
    setLotExpiry('');
    setLotConfigValues({});
  }, [productId, value?.variant_id]);

  const { data: lots = [], isSuccess: lotsLoaded } = useStockLots(
    showLot && productId
      ? { product_id: productId, variant_id: value?.variant_id ?? undefined, match_variant: !!value?.variant_id }
      : null,
  );

  // SEC-5: when the lot layer is suppressed because the product has no lots,
  // a previously-picked lot_id would be stranded on the value — invisible but
  // still submitted on the order line, leaving a phantom lot reference on the
  // stock route. Clear it once the lot query resolves empty. Gated on
  // `lotsLoaded` so a valid selection isn't wiped before the query settles.
  useEffect(() => {
    if (mode === 'pick-lot' && showLot && lotsLoaded && lots.length === 0 && value?.lot_id) {
      onChange({ ...value, lot_id: null });
    }
  }, [mode, showLot, lotsLoaded, lots.length, value, onChange]);

  const handleCreateLot = async () => {
    // Lot creation requires the picked product; capture it so it survives awaits.
    if (!value?.product_id || !lotCode.trim()) return;
    const productIdForLot = value.product_id;
    const configValues: Record<string, unknown> = {};
    for (const config of lotConfigs) {
      const vals = lotConfigValues[config.id];
      if (vals && vals.length > 0) {
        configValues[config.code] = config.config_type === 'multi_select' ? vals : vals[0];
      }
    }
    try {
      const lot = await createLot.mutateAsync({
        product_id: productIdForLot,
        variant_id: value.variant_id ?? undefined,
        lot_code: lotCode.trim(),
        expiry_date: lotExpiry || undefined,
        config_values: configValues,
      });
      onChange({ ...value, lot_id: lot.id });
      toast.success(t('products:reference.lotCreated'));
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  // --- handlers -------------------------------------------------------------
  const handleProductChange = (nextProductId: string | null, offeringId: string | null) => {
    onChange(nextProductId ? emptyProductReference(nextProductId, offeringId) : null);
  };

  const handleServiceSelect = (offering: Offering) => {
    // A freshly-chosen service offering starts with no bound instance; the
    // consumer's instance sub-step sets operational_instance_id (nullable — a
    // service-only line degrades to an ordinary line per §5).
    onChange(emptyServiceOfferingReference(offering.id));
  };

  const handlePackagingChange = (packagingId: string | null) => {
    if (!value) return;
    // A picked lot is shown in the packaging's unit, so a packaging change
    // invalidates the selection — clear it when the lot layer is active.
    onChange({
      ...value,
      packaging_id: packagingId,
      lot_id: mode === 'pick-lot' ? null : value.lot_id,
    });
  };

  const handleLotPick = (lotId: string | null) => {
    if (!value) return;
    onChange({ ...value, lot_id: lotId });
  };

  const handlePostPickChange = (config: ProductConfiguration, values: string[]) => {
    if (!value) return;
    const others = (value.configuration_selections ?? []).filter(
      (s) => s.configuration_id !== config.id,
    );
    const selection = buildSelection(config, values);
    const nextSelections = selection ? [...others, selection] : others;
    onChange({ ...value, configuration_selections: nextSelections.length > 0 ? nextSelections : null });
  };

  const postPickValuesFor = (configId: string): string[] =>
    value?.configuration_selections?.find((s) => s.configuration_id === configId)?.values ?? [];

  // --- sub-layer grouping ----------------------------------------------------
  // Mirror the per-layer render guards so we know whether ANY sub-layer is
  // visible (and therefore whether to render the indented hierarchy wrapper).
  const lotPickVisible = !!value && showLot && mode === 'pick-lot' && lots.length > 0;
  const lotCreateVisible = !!value && showLot && mode === 'create-lot';
  const hasSubLayers =
    !!value &&
    (showVariants ||
      showPackaging ||
      lotPickVisible ||
      lotCreateVisible ||
      showBundleFulfilmentNote ||
      showPostPick);
  // Indent only in the roomy stacked dialog layout — never in the dense
  // line-item layout, which opts out of labels and stays flat (the horizontal
  // exception). `space-y-3` keeps the in-group spacing identical to the prior
  // flat stack, so the only visual change is the rail + indent.
  const nestSubLayers = showLabels && hasSubLayers;

  // -------------------------------------------------------------------------
  return (
    <div className={cn('space-y-3', className)}>
      {/* Layer 1 — product */}
      <div className="space-y-1.5">
        {showLabels && (
          <Label required={required}>
            {productLabel ?? t('products:reference.product')}
          </Label>
        )}
        <ProductSearchField
          value={productId}
          onSelect={handleProductChange}
          disabled={disabled}
          statusFilter={productStatusFilter}
          excludeProductIds={excludeProductIds}
          allowedCategoryId={allowedCategoryId}
          companyId={companyId}
          popoverWidth={popoverWidth}
          placeholder={t('products:categories.selectProduct')}
          allowCreate={allowCreate}
          enableServiceOfferings={enableServiceOfferings}
          serviceOfferingId={serviceOfferingId}
          onSelectService={handleServiceSelect}
          candidates={candidates}
          onShowAllCandidates={onShowAllCandidates}
          showAllCandidatesLabel={showAllCandidatesLabel}
        />
      </div>

      {/* Service instance sub-step — for a product-less service offering only,
          the consumer supplies the operational-instance binding (e.g. pick/create
          the QUOTED shipment realizing a freight line). Rendered under the same
          indented rail as the product sub-layers in the stacked layout. */}
      {isServiceReference && serviceOfferingId && renderServiceInstance && (
        <div
          data-testid="product-reference-service-instance"
          className={cn('space-y-3', showLabels && 'ms-1 border-s-2 border-border ps-3')}
        >
          {renderServiceInstance(serviceOfferingId)}
        </div>
      )}

      {/* Layers 2-5 — secondary to the product. In the stacked dialog layout
          they sit under an indented left-rail so they read as belonging to the
          product above; the line-item layout keeps them flat. */}
      {hasSubLayers && (
      <div
        data-testid="product-reference-sublayers"
        data-nested={nestSubLayers ? 'true' : 'false'}
        className={cn('space-y-3', nestSubLayers && 'ms-1 border-s-2 border-border ps-3')}
      >
      {/* Layer 2a — named (flat) variants: a single searchable combobox, no axes. */}
      {value && showVariants && dimensions.length === 0 && (
        <div className="space-y-1.5">
          {showLabels && <Label>{t('products:reference.variant')}</Label>}
          <FlatVariantField
            variants={flatVariants}
            value={value.variant_id ?? null}
            onSelect={(variantId) => onChange({ ...value, variant_id: variantId, lot_id: null })}
            disabled={disabled}
            popoverWidth={popoverWidth}
          />
        </div>
      )}

      {/* Layer 2b — variant dimensions */}
      {value && showVariants && dimensions.length > 0 && (
        <div className="space-y-2">
          {dimensions.map((dimension) => (
            <div key={dimension.id} className="space-y-1.5">
              {showLabels && <Label>{dimension.name}</Label>}
              <Select
                value={dimSelections[dimension.id] ?? undefined}
                onValueChange={(v) => handleDimensionChange(dimension.id, v)}
                disabled={disabled || resolveVariant.isPending}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t('products:reference.selectDimension', { dimension: dimension.name })}
                  />
                </SelectTrigger>
                <SelectContent>
                  {dimension.values.map((dv) => (
                    <SelectItem key={dv.id} value={dv.id}>
                      {dv.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
          {resolveVariant.isPending && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('products:reference.resolvingVariant')}
            </p>
          )}
        </div>
      )}

      {/* Layer 3 — packaging */}
      {value && showPackaging && (
        <div className="space-y-1.5">
          {showLabels && <Label>{t('products:reference.packaging')}</Label>}
          <Select
            value={value.packaging_id ?? BASE_PACKAGING}
            onValueChange={(v) => handlePackagingChange(v === BASE_PACKAGING ? null : v)}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={BASE_PACKAGING}>
                <span className="flex items-center gap-2">
                  <span>{t('products:packagings.picker.baseUnit')}</span>
                  <Badge variant="outline" className="text-xs">
                    {t('products:packagings.picker.baseBadge')}
                  </Badge>
                </span>
              </SelectItem>
              {[...packagings]
                .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                .map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="flex items-center gap-2">
                      <span>{p.unit_name}</span>
                      {p.quantity_in_base ? (
                        <span className="text-xs text-muted-foreground">{t('products:packaging.timesQuantity', { quantity: p.quantity_in_base })}</span>
                      ) : null}
                    </span>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Layer 4 — lot. SEC-5: a product with no lots at all is simply not
          lot-tracked — render nothing rather than a disabled "Select a lot"
          control captioned "no lots available", which reads as out-of-stock
          next to the availability hint. */}
      {value && showLot && mode === 'pick-lot' && lots.length > 0 && (
        <div className="space-y-1.5">
          {showLabels && <Label>{t('products:reference.lot')}</Label>}
          <Select
            value={value.lot_id ?? undefined}
            onValueChange={(v) => handleLotPick(v)}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('products:reference.selectLot')} />
            </SelectTrigger>
            <SelectContent>
              {lots.map((lot) => (
                <SelectItem key={lot.id} value={lot.id}>
                  <span className="flex items-center gap-2">
                    <span>{lot.lot_code}</span>
                    {lot.expiry_date && (
                      <span className="text-xs text-muted-foreground">
                        {t('products:reference.lotExpiry', { date: lot.expiry_date })}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {t('products:reference.lotOnHand', { qty: lot.quantity_on_hand_in_base })}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {value && showLot && mode === 'create-lot' && (
        <div className="space-y-2 rounded-md border border-border p-3">
          {showLabels && <Label>{t('products:reference.newLot')}</Label>}
          <Input
            value={lotCode}
            disabled={disabled}
            placeholder={t('products:reference.lotCodePlaceholder')}
            onChange={(e) => setLotCode(e.target.value)}
          />
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              {t('products:reference.expiryDate')}
            </Label>
            <Input
              type="date"
              value={lotExpiry}
              disabled={disabled}
              onChange={(e) => setLotExpiry(e.target.value)}
            />
          </div>
          {lotConfigs.map((config) => (
            <div key={config.id} className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{config.name}</Label>
              <ConfigFieldInput
                config={config}
                values={lotConfigValues[config.id] ?? []}
                onChange={(vals) =>
                  setLotConfigValues((prev) => ({ ...prev, [config.id]: vals }))
                }
                disabled={disabled}
              />
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled || !lotCode.trim() || createLot.isPending}
            onClick={handleCreateLot}
          >
            {createLot.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
            {value.lot_id ? t('products:reference.lotReady') : t('products:reference.createLot')}
          </Button>
        </div>
      )}

      {/* OFF-11 — bundle: no lot layer; availability is component-derived. */}
      {value && showBundleFulfilmentNote && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Boxes className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{t('products:reference.bundleNoLot')}</span>
        </p>
      )}

      {/* Layer 5 — post-pick configurations */}
      {value && showPostPick && (
        <div className="space-y-2">
          {postPickConfigs.map((config) => (
            <div key={config.id} className="space-y-1.5">
              {showLabels && (
                <Label required={config.is_required}>
                  {config.name}
                </Label>
              )}
              <ConfigFieldInput
                config={config}
                values={postPickValuesFor(config.id)}
                onChange={(vals) => handlePostPickChange(config, vals)}
                disabled={disabled}
              />
            </div>
          ))}
        </div>
      )}
      </div>
      )}
    </div>
  );
}
